import { useEffect } from 'react'
import axios from 'axios'
import union from 'lodash.union'
import { AssetUpload, Label } from './types'
import {
  chunkArray,
  getFileMd5,
  hexToBase64,
  getLabelsFromFilename,
  getLabelsFromXMP,
} from './utils'

interface Props {
  uploaders: AssetUpload[]
  concurrentUploads: number
  preDefinedLabels: Label[]
  productId: string
  dispatch: (action: any) => void
  createAssets: (productId: string, assets: any[]) => Promise<any>
  updateAsset: (asset: any) => Promise<any>
  deleteAsset: (asset: any) => Promise<any>
}

export function useOnUploadersToPrepare({
  uploaders,
  dispatch,
  preDefinedLabels,
}: Props) {
  useEffect(() => {
    async function prepareUploaders() {
      const uploadersToPrepare = uploaders.filter((u) => u.shouldPrepare)
      if (uploadersToPrepare.length === 0) {
        return
      }

      dispatch({
        type: 'UPDATE_UPLOADS',
        data: uploadersToPrepare.map((upload) => ({
          upload,
          data: { isPreparing: true, shouldPrepare: false },
        })),
      })

      const chunks = chunkArray(uploadersToPrepare, 10)

      for (const chunk of chunks) {
        const data = await Promise.all(
          chunk.map(async (upload) => {
            const file = upload.file as File
            const labelsFromFilename = getLabelsFromFilename(
              file.name,
              preDefinedLabels
            )

            const [md5, labelsFromXMP] = await Promise.all([
              getFileMd5(file),
              getLabelsFromXMP(file, preDefinedLabels).catch((e) => []),
            ])

            return {
              upload,
              data: {
                isPreparing: false,
                shouldPrepare: false,
                shouldCreate: true,
                md5,
                asset: { labels: union(labelsFromFilename, labelsFromXMP) },
              },
            }
          })
        )

        dispatch({
          type: 'UPDATE_UPLOADS',
          data,
        })
      }
    }

    prepareUploaders()
  }, [uploaders, dispatch, preDefinedLabels])
}

export function useOnUploadersToCreate({
  uploaders,
  dispatch,
  createAssets,
  productId,
}: Props) {
  useEffect(() => {
    async function createUploaders() {
      const uploadersToCreate = uploaders.filter((u) => u.shouldCreate)
      if (uploadersToCreate.length === 0) {
        return
      }

      dispatch({
        type: 'UPDATE_UPLOADS',
        data: uploadersToCreate.map((upload) => ({
          upload,
          data: { shouldCreate: false, isCreating: true },
        })),
      })

      try {
        const assets = uploadersToCreate.map((upload) => {
          const file = upload.file as File
          return {
            contentType: file.type,
            filename: file.name,
            size: file.size,
            md5: upload.md5,
            labels: upload.asset.labels,
            isPrimary: upload.asset.isPrimary,
            groupId: upload.asset.groupId,
            masterForGroupId: upload.asset.masterForGroupId,
          }
        })

        const createdAssets = await createAssets(productId, assets)

        dispatch({
          type: 'UPDATE_UPLOADS',
          data: uploadersToCreate.map((upload) => {
            const { uploadUrl, asset } = createdAssets.find(
              (a: any) => a.md5 === upload.md5
            )
            return {
              upload,
              data: {
                isCreating: false,
                shouldUpload: true,
                shouldUpdate: false,
                uploadUrl,
                asset,
              },
            }
          }),
        })
      } catch (error) {
        dispatch({
          type: 'UPDATE_UPLOADS',
          data: uploadersToCreate.map((upload) => ({
            upload,
            data: {
              isCreating: false,
              error,
            },
          })),
        })
      }
    }

    createUploaders()
  }, [uploaders, createAssets, dispatch, productId])
}

export function useOnUploadersToUpload({
  uploaders,
  dispatch,
  updateAsset,
  concurrentUploads,
}: Props) {
  const numberOfUploads = uploaders.filter((u) => u.isUploading).length
  const nextUpload = uploaders.find((u) => u.shouldUpload && u.asset.id)

  useEffect(() => {
    async function uploadUploaders() {
      if (numberOfUploads < concurrentUploads && nextUpload) {
        const upload = nextUpload
        if (!upload.uploadUrl || !upload.md5 || !upload.file) {
          return
        }

        dispatch({
          type: 'UPDATE_UPLOAD',
          upload,
          data: {
            shouldUpload: false,
            isUploading: true,
          },
        })

        let progressThresholdIndex = 0
        await axios
          .put(upload.uploadUrl, upload.file, {
            headers: {
              'Content-Type': upload.file.type,
              'Content-MD5': hexToBase64(upload.md5),
            },
            onUploadProgress: (event) => {
              const progress = Math.round((event.loaded * 100) / event.total)
              const progressThresholds = [5, 10, 20, 40, 60, 80, 100]
              if (progress >= progressThresholds[progressThresholdIndex]) {
                progressThresholdIndex++
                dispatch({
                  type: 'UPDATE_UPLOAD',
                  upload,
                  data: { progress },
                })
              }
            },
          })
          .catch((error) => {
            dispatch({
              type: 'UPDATE_UPLOAD',
              upload,
              data: {
                isUploading: false,
                error,
              },
            })
          })

        const { origin, pathname } = new URL(upload.uploadUrl)

        dispatch({
          type: 'UPDATE_UPLOAD',
          upload,
          data: {
            isUploading: false,
            asset: {
              storageUrl: `${origin}${pathname}`,
            },
          },
        })
      }
    }

    uploadUploaders()
  }, [nextUpload, numberOfUploads, concurrentUploads, updateAsset, dispatch])
}

export function useOnUploadersToUpdate({
  uploaders,
  dispatch,
  updateAsset,
}: Props) {
  useEffect(() => {
    async function updateUploaders() {
      const uploadersToUpdate = uploaders.filter((u) => u.shouldUpdate)
      if (uploadersToUpdate.length === 0) {
        return
      }

      dispatch({
        type: 'UPDATE_UPLOADS',
        data: uploadersToUpdate.map((upload) => ({
          upload,
          data: { isUpdating: true, shouldUpdate: false },
        })),
      })

      for (const upload of uploadersToUpdate) {
        await updateAsset({
          id: upload.asset.id,
          isPrimary: upload.asset.isPrimary,
          labels: upload.asset.labels,
          groupId: upload.asset.groupId,
          masterForGroupId: upload.asset.masterForGroupId,
          url: upload.asset.storageUrl,
          comment: upload.asset.comment,
        })
          .then((asset) => {
            dispatch({
              type: 'UPDATE_UPLOAD',
              upload,
              data: {
                asset,
                isUpdating: false,
                shouldUpdate: false,
              },
            })
          })
          .catch((error) => {
            dispatch({
              type: 'UPDATE_UPLOAD',
              upload,
              data: {
                isUpdating: false,
                shouldUpdate: false,
                error,
              },
            })
          })
      }
    }

    updateUploaders()
  }, [uploaders, updateAsset, dispatch])
}

export function useOnUploadersToDelete({
  uploaders,
  deleteAsset,
  dispatch,
}: Props) {
  useEffect(() => {
    async function deleteUploaders() {
      const uploadersToDelete = uploaders.filter((u) => u.shouldDelete)
      if (uploadersToDelete.length === 0) {
        return
      }

      dispatch({
        type: 'UPDATE_UPLOADS',
        data: uploadersToDelete.map((upload) => ({
          upload,
          data: { isDeleting: true, shouldDelete: false, shouldUpdate: false },
        })),
      })

      for (const upload of uploadersToDelete) {
        if (upload.asset.id) {
          deleteAsset(upload.asset).then(() =>
            dispatch({ type: 'REMOVE_GROUP', group: upload.asset.groupId })
          )
        } else {
          dispatch({ type: 'REMOVE_GROUP', group: upload.asset.groupId })
        }
      }
    }

    deleteUploaders()
  }, [uploaders, deleteAsset, dispatch])
}

export default {
  useOnUploadersToPrepare,
  useOnUploadersToCreate,
  useOnUploadersToUpload,
  useOnUploadersToUpdate,
  useOnUploadersToDelete,
}
